import actions from 'actions'
import now from 'performance-now'
import { Dispatch } from 'redux'

import { menuFetcher, MenuRequestParams } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'
import { getMenuCutoffUntil } from 'actions/basket'
import { boxSummaryDeliveryDaysLoad } from 'actions/boxSummary'
import { menuCutoffUntilReceive, menuLoadComplete } from 'actions/menu'
import { menuServiceDataReceived } from 'actions/menuService'
import { loadRecipeAvailability } from 'actions/recipeAvailability'
import { isOptimizelyFeatureEnabledFactory } from 'containers/OptimizelyRollouts'
import { getRequestHeaders } from 'routes/Menu/apis/_utils'
import { getIsAdmin, getIsAuthenticated, getAccessToken, getAuthUserId } from 'selectors/auth'
import { getBasketDate } from 'selectors/basket'
import { getMenuCutoffUntil as getMenuCutOffUntilSelector } from 'selectors/root'
import { getLandingDay, getCutoffDateTime, getCutoffForDateAndSlot } from 'utils/deliveries'
import logger from 'utils/logger'

import { sendClientMetric } from '../apis/clientMetrics'
import { AddRecipeFn } from '../domains/basket'
import { GetState, Query } from './types'
import { selectCollection, getPreselectedCollectionName, setSlotFromIds } from './utils'

const chooseFirstDate = () => async (dispatch: Dispatch<any>, getState: GetState) => {
  const { date } = getLandingDay(getState())

  return dispatch(actions.basketDateChange(date))
}

const handleQueryError =
  (error: any, isMenuApiStockEnabled = false) =>
  async (dispatch: Dispatch<any>) => {
    await dispatch(actions.error(actionTypes.ORDER_SAVE, error))
    if (error === 'no-stock' && !isMenuApiStockEnabled) {
      await dispatch(loadRecipeAvailability())
    } else {
      await dispatch(actions.menuLoadMenu())
    }
    await dispatch(actions.menuLoadStock(true, isMenuApiStockEnabled))
  }

const beforeMenuLoad = (query: Query) => async (dispatch: Dispatch<any>, getState: GetState) => {
  if (getState().basket.get('orderId')) {
    const shippingAddresses = getState().user.get('shippingAddresses')

    const addressToSelect =
      shippingAddresses.find((address: any) => address.shippingDefault === true) ||
      shippingAddresses.first()

    await dispatch(actions.basketReset(addressToSelect))
  }

  if (
    query.day_id ||
    query.slot_id ||
    getBasketDate(getState()) ||
    getState().basket.get('slotId')
  ) {
    try {
      dispatch(setSlotFromIds(query.slot_id, query.day_id))
    } catch (err: any) {
      // eslint-disable-next-line prettier/prettier
      ;(logger as any).error({ message: `Debug fetchData: ${err.message}`, errors: [err] })
    }
  } else if (!getState().basket.get('date')) {
    await chooseFirstDate()(dispatch, getState)
  }

  if (query.num_portions) {
    dispatch(actions.basketNumPortionChange(query.num_portions))
  }

  if (query.postcode && !getState().basket.get('postcode')) {
    dispatch(actions.basketPostcodeChangePure(query.postcode))
  }
}

function handleExpiredBasketDate({
  dispatch,
  getState,
}: {
  dispatch: Dispatch<any>
  getState: GetState
}) {
  const basketDate = getState().basket.get('date')
  const basketSlotId = getState().basket.get('slotId')

  const cutoffDate = getCutoffForDateAndSlot(
    basketDate,
    basketSlotId,
    getState().boxSummaryDeliveryDays,
  )

  // if there is no cutoff date it means there is no available slot for the basket date
  if (!cutoffDate) {
    const { date } = getLandingDay(getState(), {
      useBasketDate: false,
    })

    dispatch(actions.basketDateChange(date))
  }
}

const loadWithoutOrder =
  (isMenuApiStockEnabled = false) =>
  async (dispatch: Dispatch<any>) => {
    await dispatch(actions.menuLoadMenu())
    if (!isMenuApiStockEnabled) {
      await dispatch(loadRecipeAvailability())
    }
    await dispatch(actions.menuLoadStock(true, isMenuApiStockEnabled))
    dispatch(actions.pending(actionTypes.MENU_FETCH_DATA, false))
  }

const selectCollectionFromQuery =
  (query: Query) => (dispatch: Dispatch<any>, getState: GetState) => {
    const state = getState()
    const collectionName = getPreselectedCollectionName(state, query.collection)
    dispatch(selectCollection(collectionName))
  }

// eslint-disable-next-line import/no-default-export
export default function fetchData(
  { query, params }: { query: Query; params: any },
  force: boolean,
  background: any,
  { addRecipe, date }: { addRecipe: AddRecipeFn; date?: string },
) {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    const startTime = now()

    const isPending = getState().pending && getState().pending.get(actionTypes.MENU_FETCH_DATA)
    const isAdminQuery = !!(query && query['preview[auth_user_id]'])

    if (!isAdminQuery && isPending) {
      return
    }

    dispatch(actions.pending(actionTypes.MENU_FETCH_DATA, true))
    dispatch(actions.error(actionTypes.MENU_FETCH_DATA, false))

    if (params.orderId) {
      dispatch(actions.basketIdChange(params.orderId))
    } else {
      const isAuthenticated = getIsAuthenticated(getState())
      const isAdmin = getIsAdmin(getState())

      if (isAuthenticated && !isAdmin) {
        await dispatch(actions.userLoadOrders())
      }
    }

    try {
      let cutOffUntil = getMenuCutOffUntilSelector(getState())
      if (!cutOffUntil) {
        // This will fetch from /active endpoint
        cutOffUntil = await getMenuCutoffUntil(getState)
        dispatch(menuCutoffUntilReceive(cutOffUntil))
        await dispatch(boxSummaryDeliveryDaysLoad(undefined, cutOffUntil))
      }

      // we need the delivery days to calculate the date to pass to the menu API based on the delivery date using the slots data
      if (params.orderId) {
        const { auth, user } = getState()
        if (auth.get('isAuthenticated') && !user.get('email') && !auth.get('isAdmin')) {
          await dispatch(actions.userLoadData())
        }
        await dispatch(actions.menuLoadOrderDetails(params.orderId, addRecipe))
      } else {
        await beforeMenuLoad(query)(dispatch, getState)
        handleExpiredBasketDate({ dispatch, getState })
      }

      let menuDate = date
      if (menuDate === undefined) {
        menuDate = (getCutoffDateTime(getState()) || '').substr(0, 10) || undefined
      }

      const accessToken = getAccessToken(getState())
      const isAuthenticated = getIsAuthenticated(getState())
      const userId = getAuthUserId(getState())

      const isMenuApiStockEnabled = await isOptimizelyFeatureEnabledFactory(
        'turnips_web_menu_api_stock_enabled',
      )(dispatch, getState)

      const menuResponse = await menuFetcher({
        accessToken,
        isAuthenticated,
        userId,
        date: menuDate as string,
        deliveryDate: isMenuApiStockEnabled ? getBasketDate(getState()) : undefined,
        headers: getRequestHeaders(userId),
        ...query,
      } as MenuRequestParams)

      if (isAdminQuery) {
        dispatch(actions.basketDateChange(menuResponse.data[0].attributes.ends_at.split('T')[0]))
      }

      dispatch(menuServiceDataReceived(menuResponse, accessToken))

      if (query.error) {
        await handleQueryError(query.error, isMenuApiStockEnabled)(dispatch)
      }

      if (params.orderId) {
        await dispatch(actions.menuLoadMenu())
        // don't fetch recipe availability again if we are fetching this info from menu api
        if (!isMenuApiStockEnabled) {
          await dispatch(loadRecipeAvailability())
        }
        // map recipe availability stock from menu api
        await dispatch(actions.menuLoadStock(true, isMenuApiStockEnabled))
        dispatch(actions.pending(actionTypes.MENU_FETCH_DATA, false))
        sendClientMetric('menu-edit-initiated', 1, 'Count')
      } else {
        await loadWithoutOrder(isMenuApiStockEnabled)(dispatch)
      }

      selectCollectionFromQuery(query)(dispatch, getState)

      const timeTaken = Math.round(now() - startTime)
      dispatch(menuLoadComplete(timeTaken, true))
    } catch (e) {
      dispatch(actions.pending(actionTypes.MENU_FETCH_DATA, false))
      dispatch(actions.error(actionTypes.MENU_FETCH_DATA, true))
      throw e
    }
  }
}
